<template>
  <header class="tb" :class="{ 'tb--miniapp': isMiniApp }">
    <router-link :to="{ name: 'forms' }" class="tb__logo">
      <span class="tb__name"><b>Опросы</b></span>
    </router-link>

    <div class="tb__spacer" />

    <!-- В мини-аппе профиль/выход даёт сам TN Life, а правый верхний угол
         занят его системными пилюлями — свой блок не показываем -->
    <div v-if="!isMiniApp" class="tb__right">
      <template v-if="me">
        <div class="tb__user-wrap">
          <button
            type="button"
            class="tb__user"
            :aria-expanded="menuOpen"
            aria-label="Меню пользователя"
            @click="menuOpen = !menuOpen"
          >
            <TNUserPicture :text="initials" size="m" :square="true" class="tb__avatar-pic" />
            <span class="tb__label">{{ me.fullName }}</span>
            <TNIcon name="down-s" :size="14" class="tb__caret" />
          </button>

          <div v-if="menuOpen" v-click-outside="closeMenu" class="tb__menu">
            <div class="tb__menu-head">
              <span class="tb__menu-name">{{ me.fullName }}</span>
              <span v-if="me.department" class="tb__menu-dep">{{ me.department }}</span>
              <span v-if="me.email" class="tb__menu-email">{{ me.email }}</span>
            </div>
            <div class="tb__menu-sep" />
            <button class="tb__menu-item" type="button" @click="handleLogout">
              <TNIcon name="backto" :size="15" />
              Выйти
            </button>
          </div>
        </div>
      </template>
      <template v-else-if="!loading">
        <a class="tb__login-link" :href="loginUrl">Войти</a>
      </template>
    </div>
  </header>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import LifeBridge from '@tnlife/tn-bridge';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const me = computed(() => authStore.me);
const loading = computed(() => authStore.loading);
const menuOpen = ref(false);
const isMiniApp = LifeBridge.isWebView || LifeBridge.isIFrame;

const initials = computed(() => {
  const n = me.value?.fullName || '';
  return n
    .split(/[\s·]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
});

const loginUrl = computed(
  () => `/auth/login?return=${encodeURIComponent(route.fullPath)}`
);

function closeMenu(): void {
  menuOpen.value = false;
}

async function handleLogout(): Promise<void> {
  closeMenu();
  await authStore.logout();
  router.push({ name: 'forms' });
}
</script>

<style scoped>
.tb {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 16px;
  height: 52px;
  min-height: 52px;
  background: var(--background-primary-a-enabled);
  border-bottom: 1px solid var(--border-secondary-enabled);
  box-shadow: 0 1px 4px rgba(15, 25, 50, 0.06);
}

.tb__logo {
  display: flex;
  align-items: center;
  gap: 8px;
  color: inherit;
  flex-shrink: 0;
}

.tb__mark {
  display: inline-flex;
  color: var(--content-accent-enabled);
}

.tb__name b {
  font-weight: var(--font-weight-heading);
  font-size: var(--font-size-body-medium);
  color: var(--content-primary-a-enabled);
}

.tb__spacer {
  flex: 1;
}

.tb__right {
  display: flex;
  align-items: center;
  /* Правый верхний угол фрейма занят иконками супераппа TN Life —
     держим профиль левее этой зоны (см. договорённость с владельцем). */
  margin-right: 100px;
}

.tb__user-wrap {
  position: relative;
}

.tb__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 4px 4px;
  border: none;
  border-radius: 10px;
  background: transparent;
  font: inherit;
  cursor: pointer;
  transition: background 0.15s ease;
}
.tb__user:hover {
  background: var(--background-secondary-enabled);
}

.tb__avatar-pic {
  flex-shrink: 0;
  border-radius: 8px !important;
  width: 34px !important;
  height: 34px !important;
}
.tb__avatar-pic :deep(.tn-user-picture__container) {
  border-radius: 8px !important;
}

.tb__label {
  font-size: var(--font-size-body-small);
  font-weight: var(--font-weight-accent);
  color: var(--content-primary-a-enabled);
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tb__caret {
  color: var(--content-secondary-enabled);
}

.tb__menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 300;
  min-width: 240px;
  padding: 6px;
  background: var(--background-primary-a-enabled);
  border: 1px solid var(--border-secondary-enabled);
  border-radius: var(--zk-radius-md);
  box-shadow: var(--zk-shadow-card);
}

.tb__menu-head {
  display: grid;
  gap: 2px;
  padding: 8px 10px;
}
.tb__menu-name {
  font-size: var(--font-size-body-small);
  font-weight: var(--font-weight-accent);
  color: var(--content-primary-a-enabled);
}
.tb__menu-dep,
.tb__menu-email {
  font-size: var(--font-size-label-medium);
  color: var(--content-secondary-enabled);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tb__menu-sep {
  height: 1px;
  margin: 4px 0;
  background: var(--border-secondary-enabled);
}

.tb__menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: var(--font-size-body-small);
  color: var(--content-primary-a-enabled);
  cursor: pointer;
  text-align: left;
}
.tb__menu-item:hover {
  background: var(--background-secondary-enabled);
}

.tb__login-link {
  font-size: var(--font-size-body-small);
  font-weight: var(--font-weight-accent);
  color: var(--content-accent-enabled);
}

@media (max-width: 768px) {
  /* Профиль на мобиле живёт в тап-баре: правый-верх фрейма — под иконки супераппа */
  .tb__user-wrap { display: none; }
  .tb__right { margin-right: 0; }
}

@media (max-width: 480px) {
  .tb { padding: 0 12px; }
}

/* Правый край свободен под системные пилюли суперапа */
.tb--miniapp { padding-right: 108px; }
</style>
